/**
 * The rules the details form holds a visitor's typing to: the SHARED field
 * library's (shared-audit#17, field-rules migration pair 2) —
 * `@puglieseweb/shared-react-ui/fields`, the TypeScript twin of
 * shared-pweb-common's `pweb_common.fields`, which `chat_lead` applies on the
 * server (backend common/field_rules.py and common/lead_form.py). One shared
 * `cases.json` holds both to the same answers (scripts/test-field-rules.mjs
 * here, tests/test_field_rules_cases.py there).
 *
 * 🚨 COPIED, NOT INSTALLED. `src/vendored/fields` is a byte-identical copy of
 * shared-react-ui's `src/lib/fields` at the commit pinned in
 * .github/field-rules.lock, and the field-rules-drift workflow fails on any
 * difference. Never edit it here: bump the pin and copy again. Not the package,
 * because this package's `.` export IS its TypeScript source (package.json
 * `exports`): a product importing it — the console draws its transcript with
 * `AssistantMarkdown` — would have to resolve `@puglieseweb/shared-react-ui/fields`
 * itself, and the console is on 0.18, which has none. The library is pure
 * TypeScript with no React and no dependency; it is bundled into every build.
 *
 * What stays the widget's own here: which KIND a visitor's number is (`any`:
 * a number the business rings back, never one we text), the country the box
 * starts on, the country list, and the paste rule. The SENTENCES are the
 * widget's five languages, as the library's `FieldMessageBundle`s
 * (fieldMessages.ts). Pure: each check returns the library's problem
 * `{code, params}`, never a sentence.
 */
import {
  PHONE_COUNTRIES as LIBRARY_COUNTRIES,
  checkPhone,
  countryName,
  normalisePhoneInput,
  phoneInputMaxLength,
  phoneRule,
  type FieldResult,
  type PhoneKind,
} from './vendored/fields';

export {
  EMAIL_MAX,
  PERSON_NAME_MAX,
  checkEmail,
  checkPersonName,
  collapseSpaces,
  counterFrom,
  type FieldCode,
  type FieldParams,
  type FieldProblem,
  type FieldResult,
} from './vendored/fields';

export { countryName };

// ── Phone (`any` kind: a number we record, never text) ──────────

/** A visitor's number is one the business rings back (#17: the `any` kind, per-country lengths only). */
export const PHONE_KIND: PhoneKind = 'any';

/** Every country the phone box offers, by ISO code: the library's table (237). */
export const PHONE_COUNTRIES: readonly string[] = LIBRARY_COUNTRIES.map((c) => c.iso);

/** The calling code of an offered country ('44'), '' for any other. */
export function dialCodeFor(country: string): string {
  return phoneRule(country ?? '', PHONE_KIND)?.dial.slice(1) ?? '';
}

/**
 * A number as typed — national ('07700 900123') or with the chosen country's
 * own code ('+44 …', '0044 …') — read for `country` by the library's `any`
 * rule: `{ok, value}` in E.164, or the problem. Nothing typed is
 * `{ok: true, value: ''}`; the caller decides whether that is allowed.
 *
 * chat_lead reads the number the same way (common/lead_form.py `lead_phone`).
 * One answer is the server's rather than the library's: a value with no digit
 * at all ('+', '()') is not a number (`phone.invalid_characters`), where the
 * library would call it a code still being typed.
 */
export function readPhone(value: string, country: string): FieldResult {
  const result = checkPhone(value ?? '', { country, kind: PHONE_KIND, required: false });
  if (!result.ok && result.problem.code !== 'phone.invalid_characters' && !/[0-9]/.test(result.value)) {
    return { ...result, problem: { ...result.problem, code: 'phone.invalid_characters' } };
  }
  return result;
}

/**
 * What goes into the box as it is typed or pasted: the chosen country's own
 * '+CC' / '00CC' in front is dropped once something follows it — the box is
 * for the national number and the country is chosen beside it (the library's
 * `normalisePhoneInput`). Any other code is left for `readPhone` to name, and
 * so is a value holding anything a number is not written with ('x42', a
 * letter): the rule names it rather than the box dropping part of it.
 */
export function stripOwnCode(value: string, country: string): string {
  const dial = dialCodeFor(country);
  if (!dial || /[^\d\s().+-]/.test(value ?? '')) return value;
  const next = normalisePhoneInput(value ?? '', `+${dial}`);
  return next.stripped ? next.value : value;
}

/**
 * The box's `maxLength`: the country's longest `any` number, the trunk prefix
 * dialled at home, three spaces (UK: 10 + 1 + 3 = 14; Italy, whose 0 is the
 * number's own: 12 + 3 = 15). A paste past it is taken WHOLE and refused in
 * words (`wholePaste`), never cut by the browser into a different number.
 */
export function phoneInputMax(country: string): number {
  return phoneInputMaxLength(country, PHONE_KIND);
}

/**
 * The country the phone box starts on: the business's own choice
 * (`leadForm.phoneCountry`), else the region the page declares (`en-US` → US,
 * `it` → IT), else the United Kingdom.
 */
export function defaultPhoneCountry(configured: string | undefined, language: string): string {
  const own = (configured ?? '').toUpperCase();
  if (dialCodeFor(own)) return own;
  const [base = '', region = ''] = (language ?? '').split(/[-_]/);
  const fromRegion = region.toUpperCase();
  if (fromRegion.length === 2 && dialCodeFor(fromRegion)) return fromRegion;
  const byBase: Record<string, string> = { en: 'GB', it: 'IT', fr: 'FR', de: 'DE', es: 'ES', pt: 'PT', nl: 'NL' };
  return byBase[base.toLowerCase()] ?? 'GB';
}

/**
 * Every country, by its name in `language`, for the list beside the phone
 * box. A page may declare a language tag no locale API accepts ('en_US', a
 * typo): the list is then sorted in the runtime's own locale rather than the
 * form failing to draw.
 */
export function countryOptions(language: string): Array<{ iso: string; name: string; dial: string }> {
  const rows = PHONE_COUNTRIES.map((iso) => ({ iso, name: countryName(iso, language), dial: dialCodeFor(iso) }));
  let compare: (a: string, b: string) => number;
  try {
    const collator = new Intl.Collator(language || undefined);
    compare = collator.compare;
  } catch {
    compare = (a, b) => a.localeCompare(b);
  }
  return rows.sort((a, b) => compare(a.name, b.name));
}

// ── Free text ─────────────────────────────────────────────────────

/**
 * What a PASTE into a box with `maxLength` should leave in it: the browser
 * cuts a paste at `maxLength` and says nothing, so a paste that would pass
 * the limit is taken whole (the form then says the limit and the count), and
 * one that fits is left to the browser (null).
 */
export function wholePaste(current: string, start: number | null, end: number | null, pasted: string, max: number): string | null {
  const from = start ?? current.length;
  const to = end ?? from;
  const next = current.slice(0, from) + pasted + current.slice(to);
  return next.length > max ? next : null;
}

